import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';

const ALL_ROLES = [
  { value: 'super_admin', label: 'Super Admin', color: '#ef4444' },
  { value: 'admin', label: 'Admin', color: '#f59e0b' },
  { value: 'dispatcher', label: 'Dispatcher', color: '#3b82f6' },
  { value: 'sales', label: 'Sales', color: '#8b5cf6' },
  { value: 'driver', label: 'Driver', color: '#10b981' },
  { value: 'maintenance', label: 'Maintenance', color: '#f97316' },
];

const MODULE_ICONS = {
  dispatch: '\uD83D\uDCCB',
  maintenance: '\uD83D\uDD27',
  leaderboard: '\uD83C\uDFC6',
  admin: '\uD83D\uDEE1\uFE0F',
  tasks: '\u2705',
  sales: '\uD83D\uDCB0',
  setup: '\u2699\uFE0F',
};

const MODULE_DESCRIPTIONS = {
  dispatch: 'Call queue, scheduling, history, reports, driver management, and dispatch configuration.',
  maintenance: 'Truck inspections, work orders, incidents, fleet status, and preventive maintenance.',
  leaderboard: 'KPI tracking, rounds, scorecards, and competitive leaderboards for teams.',
  admin: 'Employee records, company profile, facilities, and compliance tracking.',
  tasks: 'Shared to-do system. Any module can create tasks assigned to employees.',
  sales: 'Expense reimbursement, stipend tracking, notices, and account expense reporting.',
  setup: 'Users, properties, notifications, module configuration, and system settings.',
};

function ArrowUp({ disabled }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={disabled ? '#cbd5e1' : '#6d28d9'}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

function ArrowDown({ disabled }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={disabled ? '#cbd5e1' : '#6d28d9'}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function GripIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round">
      <circle cx="9" cy="6" r="1" fill="#94a3b8" /><circle cx="15" cy="6" r="1" fill="#94a3b8" />
      <circle cx="9" cy="12" r="1" fill="#94a3b8" /><circle cx="15" cy="12" r="1" fill="#94a3b8" />
      <circle cx="9" cy="18" r="1" fill="#94a3b8" /><circle cx="15" cy="18" r="1" fill="#94a3b8" />
    </svg>
  );
}

export default function ModuleSetupPage() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getModules();
      setModules(Array.isArray(data) ? data : []);
    } catch { setModules([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggleActive(mod) {
    setSaving(prev => ({ ...prev, [mod.module_key]: true }));
    setError('');
    try {
      const updated = await api.updateModule(mod.module_key, { is_active: !mod.is_active });
      setModules(prev => prev.map(m => m.module_key === mod.module_key ? updated : m));
      setSuccess(`${mod.label} ${!mod.is_active ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message); }
    setSaving(prev => ({ ...prev, [mod.module_key]: false }));
  }

  async function toggleRole(mod, role) {
    const roles = Array.isArray(mod.visible_roles) ? [...mod.visible_roles] : [];
    const idx = roles.indexOf(role);
    if (idx >= 0) roles.splice(idx, 1);
    else roles.push(role);

    // super_admin always has access
    if (!roles.includes('super_admin')) roles.unshift('super_admin');

    setSaving(prev => ({ ...prev, [mod.module_key]: true }));
    setError('');
    try {
      const updated = await api.updateModule(mod.module_key, { visible_roles: roles });
      setModules(prev => prev.map(m => m.module_key === mod.module_key ? updated : m));
    } catch (err) { setError(err.message); }
    setSaving(prev => ({ ...prev, [mod.module_key]: false }));
  }

  async function moveModule(index, direction) {
    const newModules = [...modules];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newModules.length) return;

    // Swap
    [newModules[index], newModules[targetIndex]] = [newModules[targetIndex], newModules[index]];

    // Optimistic update
    setModules(newModules);
    setReordering(true);

    try {
      const order = newModules.map((m, i) => ({ module_key: m.module_key, sort_order: i + 1 }));
      const updated = await api.reorderModules(order);
      setModules(Array.isArray(updated) ? updated : newModules);
      setSuccess('Module order updated');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.message);
      load(); // revert on failure
    }
    setReordering(false);
  }

  if (loading) return <div className="admin-page"><div className="loading-spinner">Loading...</div></div>;

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Module Setup</h1>
      </div>

      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
        Control which modules are active, which roles can see them, and their order in the sidebar. Use the arrows to rearrange modules.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div style={{ display: 'grid', gap: 16 }}>
        {modules.map((mod, index) => {
          const isSaving = saving[mod.module_key];
          const icon = MODULE_ICONS[mod.module_key] || '\uD83D\uDCE6';
          const desc = MODULE_DESCRIPTIONS[mod.module_key] || '';
          const roles = Array.isArray(mod.visible_roles) ? mod.visible_roles : [];
          const isFirst = index === 0;
          const isLast = index === modules.length - 1;

          return (
            <div key={mod.module_key} style={{
              background: '#fff', borderRadius: 14, padding: '24px',
              border: mod.is_active ? '1px solid #e2e8f0' : '1px solid #f1f5f9',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
              opacity: mod.is_active ? 1 : 0.6,
              transition: 'all 0.2s',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {/* Reorder controls */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                    <GripIcon />
                    <button
                      onClick={() => moveModule(index, -1)}
                      disabled={isFirst || reordering}
                      style={{
                        background: 'none', border: 'none', cursor: isFirst ? 'not-allowed' : 'pointer',
                        padding: 2, borderRadius: 4, display: 'flex',
                      }}
                      title="Move up"
                    >
                      <ArrowUp disabled={isFirst} />
                    </button>
                    <button
                      onClick={() => moveModule(index, 1)}
                      disabled={isLast || reordering}
                      style={{
                        background: 'none', border: 'none', cursor: isLast ? 'not-allowed' : 'pointer',
                        padding: 2, borderRadius: 4, display: 'flex',
                      }}
                      title="Move down"
                    >
                      <ArrowDown disabled={isLast} />
                    </button>
                  </div>

                  <span style={{ fontSize: 28 }}>{icon}</span>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{mod.label}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{desc}</div>
                  </div>
                </div>

                {/* On/Off toggle */}
                <button
                  onClick={() => toggleActive(mod)}
                  disabled={isSaving || mod.module_key === 'dispatch'} // dispatch always on
                  style={{
                    width: 52, height: 28, borderRadius: 14, border: 'none', cursor: mod.module_key === 'dispatch' ? 'not-allowed' : 'pointer',
                    background: mod.is_active ? '#6d28d9' : '#cbd5e1',
                    position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                  }}
                  title={mod.module_key === 'dispatch' ? 'Dispatch is always active' : (mod.is_active ? 'Disable module' : 'Enable module')}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 3,
                    left: mod.is_active ? 27 : 3,
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                  }} />
                </button>
              </div>

              {/* Role visibility */}
              {mod.is_active && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                    Visible To
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {ALL_ROLES.map(r => {
                      const isOn = roles.includes(r.value);
                      const isLocked = r.value === 'super_admin'; // always on
                      return (
                        <button
                          key={r.value}
                          onClick={() => !isLocked && toggleRole(mod, r.value)}
                          disabled={isSaving || isLocked}
                          style={{
                            padding: '6px 14px', borderRadius: 8,
                            border: isOn ? `2px solid ${r.color}` : '2px solid #e2e8f0',
                            background: isOn ? `${r.color}12` : '#fff',
                            color: isOn ? r.color : '#94a3b8',
                            fontSize: 12, fontWeight: 700, cursor: isLocked ? 'not-allowed' : 'pointer',
                            transition: 'all 0.15s',
                            opacity: isLocked ? 0.7 : 1,
                          }}
                        >
                          {r.label}
                          {isLocked && <span style={{ marginLeft: 4, fontSize: 10 }}>\uD83D\uDD12</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
